import {
	AddressByBodyshopResponse,
	IQuoteSupplierItems,
	QuoteGetItemConditionsWrapper,
	QuoteGetItemsResponse,
	QuoteNoSupplierAssignedItemResponse,
	QuoteSelectBestSupplierResponse,
	QuoteSelectSuppliersDataResponse,
	QuoteSelectSuppliersDataResponseItems,
	QuoteSupplierItemSaveCartResponse,
} from "@/main/models/response";
import { AddressTypeEnum } from "@/main/services/enum/AddressTypeEnum";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { StatusQuoteSupplierItem } from "@/main/services/enum/StatusQuoteSupplierItem";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { firstElementList } from "@/main/services/utilsArray";

import { IDamagePart, PartActionEnum } from "../damageParts/types";
import { translatePartCategoryName } from "../damageParts/utilsDamageParts";
import { IPartOffer, PartQualityEnum } from "./types";

const MAP_PART_TYPE_TO_QUALITY: Record<number, PartQualityEnum> = {
	[PartTypeEnum.OEM]: PartQualityEnum.Original,
	[PartTypeEnum.IAM]: PartQualityEnum.Iam,
	[PartTypeEnum.Generic]: PartQualityEnum.Iam,
	[PartTypeEnum.Recycled]: PartQualityEnum.Used,
};

export function getPartQuality(idPartType: number): PartQualityEnum {
	return MAP_PART_TYPE_TO_QUALITY[idPartType] ?? PartQualityEnum.Other;
}

export function isBlockedSupplierOffer(
	response: QuoteSelectBestSupplierResponse | QuoteSelectSuppliersDataResponse,
): boolean {
	return (
		Boolean(response.shipingIntegrationIsUnavailable) ||
		response.idQuoteSuppItemStatus === StatusQuoteSupplierItem.Pending ||
		Boolean(response.notQuoted) ||
		!response.price ||
		isNullUndefined(response.deliveryDays)
	);
}

export function getPartsToQuote(parts: IDamagePart[]): IDamagePart[] {
	return parts.filter((part) => part.isDamaged && part.action === PartActionEnum.Replace);
}

type TypeOfferFallback = Pick<IPartOffer, "idPartLine" | "guidPart" | "partName" | "categoryName" | "subCategoryName">;

export function buildOfferFallbackFromPart(part: IDamagePart): TypeOfferFallback {
	return {
		idPartLine: part.id,
		guidPart: part.guidPart,
		partName: part.name,
		categoryName: part.categoryName,
		subCategoryName: part.subCategoryName,
	};
}

export function buildUnavailableOffer(fallback: TypeOfferFallback): IPartOffer {
	return {
		...fallback,
		isAvailable: false,
		supplierName: "",
		guidSupplier: "",
		idPartType: 0,
		partTypeName: "",
		quality: PartQualityEnum.Other,
		price: 0,
		shippingCost: 0,
		deliveryDays: 0,
		deliveryBySupplier: false,
		guidQuoteItem: "",
		guidQuoteSupplierItem: "",
		guidSupplierInventoryWarehouse: "",
	};
}

export function mapSupplierResponseToOffer(
	fallback: TypeOfferFallback,
	response: QuoteSelectBestSupplierResponse | QuoteSelectSuppliersDataResponse,
): IPartOffer {
	const idPartType = Number(response.idPartType ?? 0);

	return {
		idPartLine: fallback.idPartLine,
		guidPart: fallback.guidPart,
		partName: response.partTitle || fallback.partName,
		categoryName: translatePartCategoryName(response.partCategoryName) || fallback.categoryName,
		subCategoryName: response.partSubCategoryName || fallback.subCategoryName,
		isAvailable: !isBlockedSupplierOffer(response),
		supplierName: response.supplier ?? "",
		guidSupplier: response.supplierGuid ?? "",
		idPartType,
		partTypeName: response.partType ?? "",
		quality: getPartQuality(idPartType),
		price: Number(response.price ?? 0),
		shippingCost: Number(response.shippingCost ?? 0),
		deliveryDays: Number(response.deliveryDays ?? 0),
		deliveryBySupplier: Boolean(response.deliveryBySupplier),
		guidQuoteItem: response.quoteItemGuid ?? "",
		guidQuoteSupplierItem: response.guidQuoteSupplierItem ?? "",
		guidSupplierInventoryWarehouse: response.supplierInventory_WarehouseGuid ?? "",
	};
}

export function mapBestSupplierToOffer(part: IDamagePart, response: QuoteSelectBestSupplierResponse): IPartOffer {
	return mapSupplierResponseToOffer(buildOfferFallbackFromPart(part), response);
}

export function applySavedCartGuids(offer: IPartOffer, response: QuoteSupplierItemSaveCartResponse): IPartOffer {
	return {
		...offer,
		guidQuoteItem: response.guidQuoteItem || offer.guidQuoteItem,
		guidQuoteSupplierItem: response.guidQuoteSupplierItem || offer.guidQuoteSupplierItem,
	};
}

function getShippingCostByPart(group: QuoteGetItemsResponse): number {
	const totalParts = group.quoteSupplierItems?.length ?? 0;
	if (!totalParts) return 0;
	if (group.deliveryBySupplier || group.shipingIntegrationIsUnavailable) return 0;

	const shipping = firstElementList(group.quotationShipingCompany?.quotationShipingCompanyItems ?? []);

	return Number(shipping?.totalAmount ?? 0) / totalParts;
}

function mapCartItemToOffer(group: QuoteGetItemsResponse, item: IQuoteSupplierItems, shippingCost: number): IPartOffer {
	const partInfo = item.quoteItem?.quoteProcurementPartInfo;
	const idPartType = Number(item.idPartType ?? 0);

	return {
		idPartLine: item.quoteItem?.guid ?? item.quoteItem?.guidPart ?? "",
		guidPart: item.quoteItem?.guidPart ?? "",
		partName: partInfo?.partName || partInfo?.supplierInventoryPartTitle || "",
		categoryName: "",
		subCategoryName: "",
		isAvailable: true,
		supplierName: group.companyName ?? "",
		guidSupplier: group.guidSupplier ?? "",
		idPartType,
		partTypeName: item.partType ?? "",
		quality: getPartQuality(idPartType),
		price: Number(item.priceTotal || item.price || 0),
		shippingCost,
		deliveryDays: Number(item.deliveryDeadline || group.deliveryDaysSupplier || 0),
		deliveryBySupplier: Boolean(group.deliveryBySupplier),
		guidQuoteItem: item.quoteItem?.guid ?? "",
		guidQuoteSupplierItem: item.guid ?? "",
		guidSupplierInventoryWarehouse: item.guidSupplierInventoryWarehouse ?? "",
	};
}

function mapPendingItemToOffer(item: QuoteNoSupplierAssignedItemResponse): IPartOffer {
	return {
		...buildUnavailableOffer({
			idPartLine: item.guidQuoteItem ?? item.guidPart ?? "",
			guidPart: item.guidPart ?? "",
			partName: item.quoteProcurementPartInfo?.partName ?? "",
			categoryName: "",
			subCategoryName: "",
		}),
		idPartType: Number(item.idPartType ?? 0),
		guidQuoteItem: item.guidQuoteItem ?? "",
	};
}

export function mapQuoteItemsToOffers(result: QuoteGetItemConditionsWrapper): IPartOffer[] {
	if (!result) return [];

	const assignedOffers = (result.assignedItems ?? []).flatMap((group) => {
		const shippingCost = getShippingCostByPart(group);

		return (group.quoteSupplierItems ?? []).map((item) => mapCartItemToOffer(group, item, shippingCost));
	});

	const pendingOffers = [
		result.unassignedItems,
		result.unansweredItems,
		result.unansweredCanceledItems,
		result.unansweredRequotedItems,
	].flatMap((condition) => (condition?.parts ?? []).map(mapPendingItemToOffer));

	return [...assignedOffers, ...pendingOffers];
}

export function isOfferOnQuote(offer: IPartOffer): boolean {
	return Boolean(offer.guidQuoteItem);
}

export function mergeOffersNotOnQuote(offersOnQuote: IPartOffer[], offers: IPartOffer[]): IPartOffer[] {
	const guidsPartOnQuote = new Set(offersOnQuote.map((offer) => offer.guidPart));
	const offersNotOnQuote = offers.filter((offer) => !isOfferOnQuote(offer) && !guidsPartOnQuote.has(offer.guidPart));

	return [...offersOnQuote, ...offersNotOnQuote];
}

export function getAvailableOffers(offers: IPartOffer[]): IPartOffer[] {
	return offers.filter((offer) => offer.isAvailable);
}

export function getUnavailableOffers(offers: IPartOffer[]): IPartOffer[] {
	return offers.filter((offer) => !offer.isAvailable);
}

export function getQuotingOffers(offers: IPartOffer[]): IPartOffer[] {
	return getUnavailableOffers(offers).filter(isOfferOnQuote);
}

export function calculateOffersTotal(offers: IPartOffer[]): number {
	return getAvailableOffers(offers).reduce((total, offer) => total + offer.price + offer.shippingCost, 0);
}

export function buildTotalLabel(offers: IPartOffer[]) {
	const total = getAvailableOffers(offers).length;

	return getTranslateByKey("information.totalWithShipping").replace("{0}", String(total));
}

export function buildDeliveryLabel(deliveryDays: number) {
	return getTranslateByKey("information.deliveryInDays").replace("{0}", String(deliveryDays));
}

export function buildAddressStreetLine(address: AddressByBodyshopResponse) {
	const { street, number } = address;

	return [street, number].filter(Boolean).join(" - ");
}

export function buildAddressCityLine(address: AddressByBodyshopResponse) {
	const { municipality } = address;
	const cityLine = [municipality?.name, municipality?.city?.name, municipality?.city?.state?.name]
		.filter(Boolean)
		.join(", ");

	return [cityLine, municipality?.zipCode].filter(Boolean).join(" - ");
}

export function getDefaultBillingAddress(
	addressData: AddressByBodyshopResponse[] | null,
): AddressByBodyshopResponse | null {
	if (!addressData) return null;

	const addressBilling = addressData.find((address) => Number(address.idAddressType) === AddressTypeEnum.Billing);

	return addressBilling ?? firstElementList(addressData);
}

export function buildBuyButtonLabel(offers: IPartOffer[], isStripe: boolean) {
	const total = getAvailableOffers(offers).length;
	if (!isStripe) return getTranslateByKey("information.generateOrder");
	if (!total) return getTranslateByKey("information.buyNow");

	return getTranslateByKey("information.buyNowWithCount").replace("{0}", String(total));
}

export function hasAnySupplierFound(responses: QuoteSelectSuppliersDataResponseItems[]): boolean {
	return responses.some((response) => (response.selectSupplierItems ?? []).length > 0);
}

export function buildPartsSentToQuoteLabel(totalParts: number) {
	if (totalParts === 1) {
		return getTranslateByKey("information.onePartSentToQuote");
	}

	return getTranslateByKey("information.partsSentToQuote").replace("{0}", String(totalParts));
}

export function getDeadlineAnswerFromItems(result: QuoteGetItemConditionsWrapper): string {
	if (!result) return "";

	const conditionWithDeadline = [
		result.unassignedItems,
		result.unansweredItems,
		result.unansweredCanceledItems,
		result.unansweredRequotedItems,
	].find((condition) => condition?.showDeadlineAnswer && condition.deadlineAnswer);

	return conditionWithDeadline?.deadlineAnswer ?? "";
}
